import React, { useEffect, useState } from 'react';
import CommonDetail from '../common/common-detail';
import CommonTableList from '../common/common-table-list';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnsType } from 'antd/es/table';
import { api_withdraw } from '../../api/product';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { withdraw, initWithdraw } from '../../model/withdraw';
import WithdrawDetail from './withdraw-detail';
import { printWithdrawDocument } from '../../utils/record-print-utils';
import { Modal } from 'antd';
import { isEmpty } from '../../utils/util';
import { describeShortLines, ShortLine } from '../../utils/negative-stock';
import { formatActor, UNKNOWN_ACTOR_SHORT } from '../../utils/audit-format';
import { deleteFailureMessage } from '../../utils/write-result';

const Withdraw: React.FC = (prop) => {
    
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState([]);
    const [withdraw, setWithdraw] = useState<withdraw>(initWithdraw);
    const [stockShort, setStockShort] = useState<ShortLine[]>([]);

    const filterField: any = ['document_no'];

    const placeholder_txt: string = 'เลขที่เอกสาร' 

    const onPrintWithdraw = async (record: any) => {
        let fullData = record;
        if (!record.detail || record.detail.length === 0) {
            try {
                const res = await api_withdraw('GET', {}, record.id);
                if (res && res.id) fullData = res;
            } catch (e) {
                console.error('Error fetching withdraw detail for print:', e);
            }
        }
        printWithdrawDocument(fullData);
    };

    const columns: ColumnsType<any> = [
        { title: 'เลขที่เอกสาร', dataIndex: 'document_no', key: 'document_no', width: '200px', fixed: 'left' },
        { title: 'วันที่เบิก', dataIndex: 'create_date', key: 'create_date', width: '200px', },
        { title: 'วันที่แก้ไข', dataIndex: 'update_date', key: 'update_date', width: '200px', },
        {
            // Phase 7C-6C: `create_by` was never written before 7C-6B, so
            // this column was blank on every row ever created. New rows
            // carry a real name; older ones say so rather than showing an
            // empty cell that reads like missing data.
            title: 'ผู้ที่เบิก', dataIndex: 'create_by_name', key: 'create_by_name',
            render: (value: string | null) => (
                <span data-testid="withdraw-actor">{formatActor(value, UNKNOWN_ACTOR_SHORT)}</span>
            )
        },
        { title: 'จำนวน', dataIndex: 'quantity', key: 'quantity' },
        { title: 'น้ำหนัก', dataIndex: 'weight', key: 'weight' },
        { title: 'ต้นทุน', dataIndex: 'cost', key: 'cost' },
        { title: 'การดำเนินการ', dataIndex: 'operation', width: '180px', fixed: 'right', render: (_, record) =>
            data.length >= 1 ? ( 
                <div className='iconOperation'>
                    <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => { onShowDetail('VIEW', record) }} />
                    <FontAwesomeIcon icon="print" title="พิมพ์ใบเบิกสินค้า" onClick={() => { onPrintWithdraw(record) }} />
                    <FontAwesomeIcon icon="pen-to-square" title="แก้ไข" onClick={() => { onShowDetail('EDIT', record); }} />
                    <FontAwesomeIcon icon="trash-can" title="ลบ" onClick={() => { onDelete(record.id) }} />
                </div>
            ) : null
        }
    ]

    const init = async () => {
        setData([]);
        let getWithdraw = await api_withdraw('LIST', {});
        setData(getWithdraw);
    }

    const getConfigModal = (titile: string, content: string) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
        }
    }

    const onShowDetail = (mode: string, withdrawData?: withdraw) => {
        if (mode === 'ADD' || mode === 'COPY') {
            setTitle('เพิ่มการเบิกสินค้า')
        } else if (mode === 'EDIT') {
            setTitle('แก้ไขการเบิกสินค้า')    
        } else if (mode === 'VIEW') {
            setTitle('รายละเอียดการเบิกสินค้า')
        }

        setMode(mode);
        setWithdraw(withdrawData !== undefined && withdrawData !== null ? withdrawData : initWithdraw);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    /**
     * Going below zero is allowed on purpose for now -- stock levels have
     * not been entered yet -- so this asks rather than refuses. When the
     * shop-wide "ขาย/เบิกสินค้าติดลบได้" setting exists, an unticked box
     * turns this into a refusal; the wording already tells the user what
     * is about to happen either way.
     */
    const confirmNegativeStock = (): Promise<boolean> => {
        if (stockShort.length === 0) return Promise.resolve(true);
        return new Promise((resolve) => {
            Modal.confirm({
                ...getConfigModal(
                    'เบิกเกินจำนวนที่มีในสต๊อก',
                    `${describeShortLines(stockShort)} — บันทึกแล้วสต๊อกจะติดลบ ต้องการบันทึกต่อหรือไม่ ?`,
                ),
                okText: 'บันทึกต่อ',
                cancelText: 'กลับไปแก้ไข',
                onOk() { resolve(true); },
                onCancel() { resolve(false); },
            });
        });
    };

    const onSave = async () => {
        if (!(await confirmNegativeStock())) return;

        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(withdraw));
        let validate = true;
        if (withdraw.id > 0) {
            operation = 'UPDATE';
            key_id = withdraw.id;

            if (body['remark'] == "") {
                validate = false;
                Modal.warning({
                    ...getConfigModal('แจ้งเตือน', 'กรุณากรอกหมายเหตุในการแก้ไข')
                });
                return false;
            }
        }


        if (body['detail'] && body['detail'].length > 0 && validate) {
            body['quantity'] = isEmpty(body['quantity']) ? 0 : body['quantity']
            body['weight'] = isEmpty(body['weight']) ? 0 : body['weight']
            body['cost'] = isEmpty(body['cost']) ? 0 : body['cost']
            body['remark'] = isEmpty(body['remark']) ? null : body['remark']

            let res = await api_withdraw(operation, body, key_id);
            if (res === 1) {
                setOpen(false);
                Modal.success({
                    ...getConfigModal('สำเร็จ', operation === 'UPDATE' ? 'แก้ไขข้อมูลสำเร็จแล้ว' : 'เพิ่มข้อมูลสำเร็จแล้ว'),
                    ...{ onOk() { init(); }}
                });
            } else {
                Modal.error({
                    ...getConfigModal('ผิดพลาด', operation === 'UPDATE' ? 'แก้ไขข้อมูลไม่สำเร็จ' : 'เพิ่มข้อมูลไม่สำเร็จ'),
                });
            }
        } else {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน')
            });
        }

    }

    const onDelete = async (key_id: number) => {
        Modal.confirm({
            ...getConfigModal('แจ้งเตือน', 'คุณต้องการลบข้อมูลนี้หรือไม่ ?'),
            ...{
                async onOk() { 
                    try {
                        let res = await api_withdraw('DELETE', {}, key_id);
                        if (res === 1) {
                            Modal.success({
                                ...getConfigModal('สำเร็จ', 'ลบข้อมูลสำเร็จแล้ว'),
                                ...{ onOk() { init(); }}
                            });
                        } else {
                            Modal.error({
                                ...getConfigModal('ผิดพลาด', 'ลบข้อมูลไม่สำเร็จ'),
                            });
                        }
                    } catch (err: any) {
                        // Backend answers a record that is already gone with a
                        // specific 404 and a sentence written for the user. Showing
                        // only "ลบข้อมูลไม่สำเร็จ" threw that away and left them
                        // guessing whether it was the network, their permissions, or
                        // a colleague who deleted it first.
                        Modal.error({
                            ...getConfigModal('ผิดพลาด', deleteFailureMessage(err, 'ลบข้อมูลไม่สำเร็จ')),
                        });
                    }
                }
            }
        });
    }

    const onDataDetailChange = (payload: any) => {
        setWithdraw(payload)
    }

    useEffect(() => {
        init();
    }, []);

    return (
        <div className="pm-page-container">
            <div className="pm-header-row">
                <div className="pm-header-title-box">
                    <h2>
                        <FontAwesomeIcon icon="dolly" style={{ color: 'var(--pm-primary, #FF7A59)' }} />
                        เบิกสินค้าบริการ / ใช้ในร้าน (Withdraw Service)
                    </h2>
                    <p>บันทึกการเบิกสินค้าออกจากสต๊อกเพื่อใช้ภายในร้านหรือการให้บริการ</p>
                </div>
            </div>

            <CommonTableList page='withdraw' placeholder={placeholder_txt} columns={columns} data={[...data]} filterField={filterField} onAdd={() => { onShowDetail('ADD') }} />
            <CommonDetail
                width='70%'
                mode={mode}
                title={title}
                open={open}
                onClosed={onCloseDetail}
                onSaved={onSave}
                recordActor={{ name: (withdraw as any)?.create_by_name, at: (withdraw as any)?.create_date }}
                element={ <WithdrawDetail withdraw={{...withdraw}} onChange={onDataDetailChange} onStockWarning={setStockShort} onNotFound={() => { setOpen(false); init(); }} /> }
            />
        </div>
    );
}

export default Withdraw;